import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { StandardTitle } from './StandardTitle';
import { formatSummaryNumber } from '@/lib/formatSummaryNumber';

const styles = StyleSheet.create({
    container: {
        padding: 10,
        marginBottom: 10
    },
    pageBreak: {
        marginTop: 20,
        marginBottom: 20,
        borderTopWidth: 1,
        borderTopColor: '#CCCCCC',
        borderTopStyle: 'dashed',
    },
    heading: {
        fontSize: 14,
        fontWeight: 'bold',
        marginBottom: 10,
        marginTop: 5,
    },
    subHeading: {
        fontSize: 12,
        fontWeight: 'bold',
        marginBottom: 5,
    },
    table: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#ECECEC',
        borderStyle: 'solid',
        marginBottom: 10,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#ECECEC',
        borderBottomStyle: 'solid',
    },
    nonBreakingRow: {
        flexShrink: 0,
    },
    tableHeader: {
        backgroundColor: '#F0F4F8',
    },
    tableCell: {
        padding: 5,
        fontSize: 8,
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
        flex: 1,
    },
    statCell: {
        width: '12%',
        textAlign: 'center',
    },
    palletCell: {
        width: '20%',
        fontWeight: 'bold',
    },
    sideCell: {
        width: '15%',
        textAlign: 'center',
    },
    statusPass: {
        color: '#4CAF50',
        fontWeight: 'bold',
    },
    statusFail: {
        color: '#F44336',
        fontWeight: 'bold',
    },
    statusNA: {
        color: '#9E9E9E',
        fontStyle: 'italic',
    },
    statusIncomplete: {
        color: '#F79E19',
        fontWeight: 'bold',
    },
    noData: {
        fontSize: 10,
        fontStyle: 'italic',
        textAlign: 'center',
        marginTop: 10,
    },
    // New styles for lean sections
    statisticsTable: {
        width: '100%',
        flexShrink: 0,
        borderWidth: 1,
        borderColor: '#ECECEC',
        borderStyle: 'solid',
        marginBottom: 8,
    },
    additionalStatsTable: {
        width: '100%',
        flexShrink: 0,
        borderWidth: 1,
        borderColor: '#ECECEC',
        borderStyle: 'solid',
        marginTop: 8,
        marginBottom: 10,
    },
    statisticCell: {
        width: '12%',
        textAlign: 'center',
        fontWeight: 'bold',
    },
    palletTypeCell: {
        width: '16%',
        textAlign: 'center',
    },
    passCell: {
        backgroundColor: '#4CAF50',
        color: 'white',
    },
    failCell: {
        backgroundColor: '#F44336',
        color: 'white',
    },
    missingDataCell: {
        backgroundColor: '#F79E19',
        color: 'white',
    },
    rowLabelCell: {
        width: '15%',
        textAlign: 'left',
    },
    dataFillStatusCell: {
        width: '15%',
        textAlign: 'center',
    },
    overallStatusCell: {
        width: '15%',
        textAlign: 'center',
    },
    failCountCell: {
        width: '7%',
        textAlign: 'center',
    },
    passCountCell: {
        width: '7%',
        textAlign: 'center',
    },
    whiteText: {
        color: 'white',
    },
    // FTL specific styles
    ftlTable: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#ECECEC',
        borderStyle: 'solid',
        marginBottom: 8,
    },
    ftlCell: {
        padding: 3,
        fontSize: 6,
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
        textAlign: 'center',
    },
    ftlHeaderCell: {
        backgroundColor: '#F0F4F8',
        padding: 3,
        fontSize: 6,
        fontWeight: 'bold',
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
        textAlign: 'center',
    },
    ftlPalletCell: {
        width: '10%',
        textAlign: 'center',
    },
    ftlDetailCell: {
        width: '8%',
        textAlign: 'center',
    },
    ftlRangeCell: {
        width: '10%',
        textAlign: 'center',
    },
    ftlCountCell: {
        width: '8%',
        textAlign: 'center',
    },
    ftlMeasuredCell: {
        width: '8%',
        textAlign: 'center',
    },
    ftlTargetCell: {
        width: '9%',
        textAlign: 'center',
    },
    // ULP specific styles
    ulpTable: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#ECECEC',
        borderStyle: 'solid',
        marginBottom: 8,
    },
    ulpCell: {
        padding: 3,
        fontSize: 6,
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
        textAlign: 'center',
    },
    ulpHeaderCell: {
        backgroundColor: '#F0F4F8',
        padding: 3,
        fontSize: 6,
        fontWeight: 'bold',
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
        textAlign: 'center',
    },
    ulpRowLabelCell: {
        width: '15%',
        textAlign: 'center',
    },
    ulpStatCell: {
        width: '17%',
        textAlign: 'center',
    },
    redCell: {
        backgroundColor: '#F44336',
        color: 'white',
    },
    greenCell: {
        backgroundColor: '#4CAF50',
        color: 'white',
    },
    // ULH specific styles
    ulhTable: {
        width: '100%',
        borderWidth: 1,
        borderColor: '#ECECEC',
        borderStyle: 'solid',
        marginBottom: 8,
    },
    ulhCell: {
        padding: 3,
        fontSize: 6,
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
        textAlign: 'center',
    },
    ulhHeaderCell: {
        backgroundColor: '#F0F4F8',
        padding: 3,
        fontSize: 6,
        fontWeight: 'bold',
        borderRightWidth: 1,
        borderRightColor: '#ECECEC',
        borderRightStyle: 'solid',
        textAlign: 'center',
    },
    ulhRowLabelCell: {
        width: '15%',
        textAlign: 'center',
    },
    ulhStatCell: {
        width: '17%',
        textAlign: 'center',
    },
});

interface SummaryItem {
    rowLabel: string;
    overallStatus: string;
    dataFillStatus: string;
    max: string | number;
    min: string | number;
    average: string | number;
    stdev: string | number;
    count: {
        pass: number;
        fail: number;
        total: number;
    };
    // Add properties for ULP additional stats
    sideAPassCount?: number;
    sideAFailCount?: number;
    sideBPassCount?: number;
    sideBFailCount?: number;
    sideCPassCount?: number;
    sideCFailCount?: number;
    sideDPassCount?: number;
    sideDFailCount?: number;

    // Add properties for Stack ULH additional stats
    'A-BPassCount'?: number;
    'A-BFailCount'?: number;
    'B-CPassCount'?: number;
    'B-CFailCount'?: number;
    'C-DPassCount'?: number;
    'C-DFailCount'?: number;
    'D-APassCount'?: number;
    'D-AFailCount'?: number;

    // Add properties to handle any string property
    [key: string]: any;
}

interface StatisticsData {
    statistics?: {
        max?: any[];
        min?: any[];
        average?: any[];
        stdev?: any[];
        count?: any[];
    };
    additionalStats?: SummaryItem[];
    // Add FTL specific properties
    rawData?: any[];
    palletDetails?: any[];
}

interface SummarySection {
    title: string;
    data: SummaryItem[];
}

interface SummaryPDFProps {
    preShipLean?: SummaryItem[] | StatisticsData;
    postShipLean?: SummaryItem[] | StatisticsData;
    unitLoadPosition?: SummaryItem[];
    preShipFTL?: SummaryItem[] | StatisticsData;
    postShipFTL?: SummaryItem[] | StatisticsData;
    stackTestLean?: SummaryItem[] | StatisticsData;
    stackTestCompression?: SummaryItem[] | StatisticsData;
    stackTestLeanByWeek?: Array<{ label: string; data: SummaryItem[] | StatisticsData }>;
    stackTestHeightByMeasurement?: Array<{ label: string; data: SummaryItem[] | StatisticsData }>;
}

export const SummaryPDF: React.FC<SummaryPDFProps> = ({
    preShipLean,
    postShipLean,
    unitLoadPosition,
    preShipFTL,
    postShipFTL,
    stackTestLean,
    stackTestCompression,
    stackTestLeanByWeek,
    stackTestHeightByMeasurement,
}) => {
    // Define array of sides for lean data
    const sides = ["Side A", "Side B", "Side C", "Side D"];
    const statisticsRows = ["Max", "Min", "Average", "StdDev", "Count"];

    // Check if data is in the new format with statistics and additionalStats
    const isNewDataFormat = (data: any): data is StatisticsData => {
        return data && (data.statistics || data.additionalStats);
    };
    console.log(preShipFTL, 'preShipFTL');
    // Check if data is in the FTL format (has palletDetails and additionalStats)
    const isFTLDataFormat = (data: any): boolean => {
        console.log(data, 'data for ftl');
        return data && data.palletDetails && data.additionalStats;
    };

    // Check if data is in the ULP format (has statistics and additionalStats)
    const isULPDataFormat = (data: any): boolean => {
        return data && data.statistics && data.additionalStats;
    };

    // Summary intentionally represents the final captured stack week only. The
    // detailed Post-Stack report is responsible for preserving each week.
    const orderedStackLeanSections = stackTestLeanByWeek
        ?.map((section, index) => {
            const weekNumber = Number.parseInt(section.label.match(/\d+/)?.[0] ?? '', 10);
            return { ...section, order: Number.isNaN(weekNumber) ? index : weekNumber };
        })
        .sort((a, b) => a.order - b.order);
    const finalStackLeanByWeek = orderedStackLeanSections?.length
        ? orderedStackLeanSections[orderedStackLeanSections.length - 1].data
        : undefined;
    const finalStackLean = stackTestLean ?? finalStackLeanByWeek;
    const stackLeanSections = [{
        title: 'Unit Load Stack Lean Weekly',
        data: isNewDataFormat(finalStackLean) ? finalStackLean : { additionalStats: finalStackLean } as StatisticsData,
    }];
    const stackHeightSections = stackTestHeightByMeasurement?.length
        ? stackTestHeightByMeasurement.map(section => ({
            title: `Stack Unit Load Height - ${section.label}`,
            data: isULPDataFormat(section.data) ? section.data : { additionalStats: section.data } as StatisticsData,
            isULH: true,
        }))
        : [{
            title: 'Stack Unit Load Height',
            data: isULPDataFormat(stackTestCompression) ? stackTestCompression : { additionalStats: stackTestCompression } as StatisticsData,
            isULH: true,
        }];

    const sections = [
        {
            title: 'Pre-Ship Lean',
            data: isNewDataFormat(preShipLean) ? preShipLean : { additionalStats: preShipLean } as StatisticsData
        },
        {
            title: 'Post-Ship Lean',
            data: isNewDataFormat(postShipLean) ? postShipLean : { additionalStats: postShipLean } as StatisticsData
        },
        {
            title: 'Unit Load Position',
            data: isULPDataFormat(unitLoadPosition) ? unitLoadPosition : { additionalStats: unitLoadPosition } as StatisticsData,
            isULP: true
        },
        {
            title: 'Pre-Ship Force To Load',
            data: isFTLDataFormat(preShipFTL) ? preShipFTL : { additionalStats: preShipFTL } as StatisticsData,
            isFTL: true
        },
        {
            title: 'Post-Ship Force To Load',
            data: isFTLDataFormat(postShipFTL) ? postShipFTL : { additionalStats: postShipFTL } as StatisticsData,
            isFTL: true
        },
        ...stackLeanSections,
        ...stackHeightSections,
    ];

    const getStatusStyle = (status: string) => {
        switch (status) {
            case 'Pass':
                return styles.statusPass;
            case 'Fail':
                return styles.statusFail;
            case 'N/A':
                return styles.statusNA;
            case 'Incomplete':
                return styles.statusIncomplete;
            default:
                return {};
        }
    };

    const getCellStyle = (value: string | number, isLeanValue: boolean = false, target: number = 2) => {
        if (value === 'N/A' || value === null || value === undefined) return {};

        // Only apply color coding to lean values that are numbers
        if (isLeanValue && typeof value === 'number') {
            const numericValue = Math.abs(value);
            if (numericValue <= target) {
                return styles.passCell;
            } else {
                return styles.failCell;
            }
        }
        return {};
    };

    const formatValue = (value: string | number) => {
        return formatSummaryNumber(value);
    };

    // Render statistics table for lean data
    const renderStatisticsTable = (sectionData: StatisticsData, title: string) => {
        const statistics = sectionData.statistics;

        if (!statistics) return null;

        return (
            <View style={styles.statisticsTable}>
                <View
                    style={[styles.tableRow, styles.tableHeader, styles.nonBreakingRow]}
                    wrap={false}
                    minPresenceAhead={40}
                >
                    <View style={[styles.tableCell, styles.statisticCell]}>
                        <Text>Statistic</Text>
                    </View>
                    <View style={[styles.tableCell, styles.palletTypeCell]}>
                        <Text>Pallet Type</Text>
                    </View>
                    {sides.map((side) => (
                        <View key={`${title}-header-${side}`} style={[styles.tableCell, styles.sideCell]}>
                            <Text>{side}</Text>
                        </View>
                    ))}
                </View>

                {statisticsRows.map((statistic, rowIndex) => {
                    const statisticKey = statistic === 'StdDev' ? 'stdev' : statistic.toLowerCase();
                    const statisticData = statistics[statisticKey as keyof typeof statistics];

                    if (!statisticData || !Array.isArray(statisticData)) return null;

                    return statisticData.map((statRow: any, statRowIndex: number) => (
                        <View
                            key={`${title}-${statistic}-${statRowIndex}`}
                            style={[styles.tableRow, styles.nonBreakingRow]}
                            wrap={false}
                        >
                            {statRowIndex === 0 && (
                                <View style={[styles.tableCell, styles.statisticCell]}>
                                    <Text>{statistic}</Text>
                                </View>
                            )}
                            {statRowIndex !== 0 && (
                                <View style={[styles.tableCell, styles.statisticCell]}>
                                    <Text></Text>
                                </View>
                            )}
                            <View style={[styles.tableCell, styles.palletTypeCell]}>
                                <Text>{statRow.palletType}</Text>
                            </View>
                            {sides.map((side, sideIndex) => {
                                const sideKey = `side${String.fromCharCode(65 + sideIndex)}`;
                                const value = statRow[sideKey];
                                const isColorApplicable = ["Max", "Min", "Average"].includes(statistic);
                                const cellStyle = isColorApplicable ? getCellStyle(value, true) : {};

                                return (
                                    <View
                                        key={`${title}-${statistic}-${side}-${statRowIndex}`}
                                        style={[styles.tableCell, styles.sideCell, cellStyle]}
                                    >
                                        <Text style={isColorApplicable && (cellStyle === styles.passCell || cellStyle === styles.failCell) ? styles.whiteText : {}}>
                                            {formatValue(value)}
                                        </Text>
                                    </View>
                                );
                            })}
                        </View>
                    ));
                })}
            </View>
        );
    };

    // Render additional stats table for lean data
    const renderAdditionalStatsTable = (sectionData: StatisticsData, title: string) => {
        const additionalStats = sectionData.additionalStats;

        if (!additionalStats || !Array.isArray(additionalStats) || additionalStats.length === 0) {
            return <Text style={styles.noData}>No additional statistics available for {title}</Text>;
        }

        return (
            <View style={styles.additionalStatsTable}>
                <View
                    style={[styles.tableRow, styles.tableHeader, styles.nonBreakingRow]}
                    wrap={false}
                    minPresenceAhead={40}
                >
                    <View style={[styles.tableCell, styles.rowLabelCell]}>
                        <Text>Row Labels</Text>
                    </View>
                    <View style={[styles.tableCell, styles.dataFillStatusCell]}>
                        <Text>Data Fill Status</Text>
                    </View>
                    <View style={[styles.tableCell, styles.overallStatusCell]}>
                        <Text>Overall pass/fail status</Text>
                    </View>
                    {sides.map((side) => (
                        <React.Fragment key={`${title}-addstats-header-${side}`}>
                            <View style={[styles.tableCell, styles.failCountCell]}>
                                <Text>{side} Fail Count</Text>
                            </View>
                            <View style={[styles.tableCell, styles.passCountCell]}>
                                <Text>{side} Pass Count</Text>
                            </View>
                        </React.Fragment>
                    ))}
                </View>

                {additionalStats.map((row: SummaryItem, index: number) => {
                    // Determine cell style for overall status
                    const overallStatusStyle =
                        row.overallStatus === 'Fail' ? styles.failCell :
                            (row.overallStatus === 'Pass' && row.dataFillStatus !== 'Incomplete') ? styles.passCell :
                                styles.missingDataCell;

                    return (
                        <View
                            key={`${title}-addstats-row-${index}`}
                            style={[styles.tableRow, styles.nonBreakingRow]}
                            wrap={false}
                        >
                            <View style={[styles.tableCell, styles.rowLabelCell]}>
                                <Text>{row.rowLabel}</Text>
                            </View>
                            <View style={[styles.tableCell, styles.dataFillStatusCell]}>
                                <Text>{row.dataFillStatus}</Text>
                            </View>
                            <View style={[styles.tableCell, styles.overallStatusCell, overallStatusStyle]}>
                                <Text style={styles.whiteText}>
                                    {(row.overallStatus === 'Pass' && row.dataFillStatus !== 'Incomplete') ? row.overallStatus :
                                        row.overallStatus === 'Fail' ? row.overallStatus : "Data missing"}
                                </Text>
                            </View>

                            {sides.map((side, sideIndex) => {
                                const sideLetter = String.fromCharCode(65 + sideIndex);
                                const failCountKey = `side${sideLetter}FailCount` as keyof SummaryItem;
                                const passCountKey = `side${sideLetter}PassCount` as keyof SummaryItem;

                                const failCount = row[failCountKey] as number || 0;
                                const passCount = row[passCountKey] as number || 0;

                                const failCellStyle = failCount > 0 ? styles.failCell : styles.passCell;
                                const passCellStyle = passCount > 0 ? styles.passCell : styles.failCell;

                                return (
                                    <React.Fragment key={`${title}-addstats-${side}-${index}`}>
                                        <View style={[styles.tableCell, styles.failCountCell, failCellStyle]}>
                                            <Text style={styles.whiteText}>{failCount}</Text>
                                        </View>
                                        <View style={[styles.tableCell, styles.passCountCell, passCellStyle]}>
                                            <Text style={styles.whiteText}>{passCount}</Text>
                                        </View>
                                    </React.Fragment>
                                );
                            })}
                        </View>
                    );
                })}
            </View>
        );
    };

    // Add renderFTLPalletDetailsTable function
    const renderFTLPalletDetailsTable = (sectionData: StatisticsData, title: string) => {
        const palletDetails = sectionData.palletDetails;
        console.log(palletDetails, 'palletDetails');
        if (!palletDetails || !Array.isArray(palletDetails) || palletDetails.length === 0) {
            return <Text style={styles.noData}>No pallet details available for {title}</Text>;
        }

        return (
            <View style={styles.ftlTable}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <View style={[styles.ftlCell, styles.ftlPalletCell]} >
                        <Text>Pallet Type</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Weight</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Calculated FTL Target</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Plus By User</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Percentage</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlTargetCell]} >
                        <Text>Top Calculated Target</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Top Lower Range</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Top Upper Range</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlTargetCell]} >
                        <Text>Middle Calculated Target</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Middle Lower Range</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Middle Upper Range</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlTargetCell]} >
                        <Text>Bottom Calculated Target</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Bottom Lower Range</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Bottom Upper Range</Text>
                    </View>
                </View>

                {palletDetails.map((pallet: any, index: number) => (
                    <View key={`${title}-pallet-details-${index}`} style={styles.tableRow}>
                        <View style={[styles.ftlCell, styles.ftlPalletCell]}>
                            <Text>{pallet.palletName}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{pallet.weight}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Number(pallet.calculatedFTLTarget).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{pallet.plusByUser}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{pallet.percentage}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlTargetCell]}>
                            <Text>{Number(pallet.topCalculatedTarget).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Number(pallet.topMin).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{formatSummaryNumber(parseFloat(pallet.topCalculatedTarget) + parseFloat(pallet.plusByUser))}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlTargetCell]}>
                            <Text>{Number(pallet.middleCalculatedTarget).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Number(pallet.middleMin).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{formatSummaryNumber(Number(pallet.middleCalculatedTarget) + Number(pallet.plusByUser))}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlTargetCell]}>
                            <Text>{Number(pallet.bottomCalculatedTarget).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Number(pallet.bottomMin).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{formatSummaryNumber(Number(pallet.bottomCalculatedTarget) + Number(pallet.plusByUser))}</Text>
                        </View>
                    </View>
                ))}
            </View>
        );
    };

    // Add renderFTLStatisticsTable function
    const renderFTLStatisticsTable = (sectionData: StatisticsData, title: string) => {
        const statistics = sectionData.statistics;

        if (!statistics || !statistics.min) return null;

        return (
            <View style={styles.ftlTable}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <View style={[styles.ftlCell, styles.ftlPalletCell]} >
                        <Text>Pallet Type</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Top Min</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Top Max</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Top StDev</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Middle Min</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Middle Max</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Middle StDev</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Bottom Min</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Bottom Max</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlDetailCell]} >
                        <Text>Bottom StDev</Text>
                    </View>
                </View>

                {statistics.min.map((row: any, index: number) => (
                    <View key={`${title}-stats-${index}`} style={styles.tableRow}>
                        <View style={[styles.ftlCell, styles.ftlPalletCell]}>
                            <Text>{row.palletName}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(row.top)).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(statistics.max[index].top)).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(statistics.stdev[index].top)).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(row.middle)).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(statistics.max[index].middle)).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(statistics.stdev[index].middle)).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(row.bottom)).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(statistics.max[index].bottom)).toFixed(2)}</Text>
                        </View>
                        <View style={[styles.ftlCell, styles.ftlDetailCell]}>
                            <Text>{Math.abs(parseFloat(statistics.stdev[index].bottom)).toFixed(2)}</Text>
                        </View>
                    </View>
                ))}
            </View>
        );
    };

    // Add renderFTLAdditionalStatsTable function
    const renderFTLAdditionalStatsTable = (sectionData: StatisticsData, title: string) => {
        const additionalStats = sectionData.additionalStats;

        if (!additionalStats || !Array.isArray(additionalStats) || additionalStats.length === 0) {
            return <Text style={styles.noData}>No additional statistics available for {title}</Text>;
        }

        return (
            <View style={styles.ftlTable}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <View style={[styles.ftlCell, styles.ftlPalletCell]} >
                        <Text>Row Labels</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Count of Pallet Name</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Count of Top Measured</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Count of Middle Measured</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Count of Bottom Measured</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Data Fill Status</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlRangeCell]} >
                        <Text>FTL Range</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Top Above Recommended Count</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Top Below Recommended Count</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Middle Above Recommended Count</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Middle Below Recommended Count</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Bottom Above Recommended Count</Text>
                    </View>
                    <View style={[styles.ftlCell, styles.ftlCountCell]} >
                        <Text>Bottom Below Recommended Count</Text>
                    </View>
                </View>

                {additionalStats.map((row: any, index: number) => {
                    // Determine cell style for FTL Range
                    const ftlRangeStyle =
                        row.ftlRange === 'Fail' ? styles.failCell :
                            (row.ftlRange === 'Pass' && row.dataFillStatus !== 'Incomplete') ? styles.passCell :
                                styles.missingDataCell;

                    // Helper to determine cell style for count cells
                    const getCountCellStyle = (count: number) => count > 0 ? styles.failCell : styles.passCell;

                    return (
                        <View key={`${title}-addstats-${index}`} style={styles.tableRow}>
                            <View style={[styles.ftlCell, styles.ftlPalletCell]}>
                                <Text>{row.rowLabel}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell]}>
                                <Text>{row.countOfPalletName}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell]}>
                                <Text>{row.countOfTopMeasured}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell]}>
                                <Text>{row.countOfMiddleMeasured}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell]}>
                                <Text>{row.countOfBottomMeasured}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell]}>
                                <Text>{row.dataFillStatus}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlRangeCell, ftlRangeStyle]}>
                                <Text style={styles.whiteText}>
                                    {row.ftlRange === 'Pass' && row.dataFillStatus === 'Incomplete' ?
                                        "Data Missing" : row.ftlRange}
                                </Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell, getCountCellStyle(parseInt(row.topAboveRecommendedCount))]}>
                                <Text style={styles.whiteText}>{row.topAboveRecommendedCount}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell, getCountCellStyle(parseInt(row.topBelowRecommendedCount))]}>
                                <Text style={styles.whiteText}>{row.topBelowRecommendedCount}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell, getCountCellStyle(parseInt(row.middleAboveRecommendedCount))]}>
                                <Text style={styles.whiteText}>{row.middleAboveRecommendedCount}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell, getCountCellStyle(parseInt(row.middleBelowRecommendedCount))]}>
                                <Text style={styles.whiteText}>{row.middleBelowRecommendedCount}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell, getCountCellStyle(parseInt(row.bottomAboveRecommendedCount))]}>
                                <Text style={styles.whiteText}>{row.bottomAboveRecommendedCount}</Text>
                            </View>
                            <View style={[styles.ftlCell, styles.ftlCountCell, getCountCellStyle(parseInt(row.bottomBelowRecommendedCount))]}>
                                <Text style={styles.whiteText}>{row.bottomBelowRecommendedCount}</Text>
                            </View>
                        </View>
                    );
                })}
            </View>
        );
    };

    // Fix renderULPStatisticsTable function to match ship-test-walk.tsx component more closely
    const renderULPStatisticsTable = (sectionData: StatisticsData, title: string) => {
        const statistics = sectionData.statistics;

        if (!statistics) return null;

        return (
            <View style={styles.ulpTable}>
                {/* First table - Statistics */}
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <View style={[styles.ulpCell, { width: '16%' }]}>
                        <Text>Statistic</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '16%' }]}>
                        <Text>Pallet Type</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '17%' }]}>
                        <Text>Side A Position (in)</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '17%' }]}>
                        <Text>Side B Position (in)</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '17%' }]}>
                        <Text>Side C Position (in)</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '17%' }]}>
                        <Text>Side D Position (in)</Text>
                    </View>
                </View>

                {['max', 'min', 'average', 'stdev', 'count'].map((statType) => {
                    const statData = statistics[statType];
                    if (!statData || !Array.isArray(statData)) return null;

                    return statData.map((row: any, rowIndex: number) => {
                        // For postShip variant, add red highlighting for values > 1
                        const isPostShip = title.includes('Position'); // ULP is always postShip
                        const isCountRow = statType === 'count';

                        const getSideStyle = (value: string) => {
                            if (!isPostShip || isCountRow) return {};
                            return parseFloat(value) > 1 ? styles.redCell : {};
                        };

                        return (
                            <View key={`${title}-${statType}-${rowIndex}`} style={[styles.tableRow, { backgroundColor: '#f9f9f9' }]}>
                                {rowIndex === 0 && (
                                    <View style={[styles.ulpCell, { width: '16%', fontWeight: 'bold' }]}>
                                        <Text>{statType.charAt(0).toUpperCase() + statType.slice(1)}</Text>
                                    </View>
                                )}
                                {rowIndex !== 0 && (
                                    <View style={[styles.ulpCell, { width: '16%' }]}>
                                        <Text></Text>
                                    </View>
                                )}
                                <View style={[styles.ulpCell, { width: '16%' }]}>
                                    <Text>{row.palletType}</Text>
                                </View>
                                <View style={[styles.ulpCell, { width: '17%' }, getSideStyle(row.sideA)]}>
                                    <Text style={getSideStyle(row.sideA) === styles.redCell ? styles.whiteText : undefined}>
                                        {row.sideA}
                                    </Text>
                                </View>
                                <View style={[styles.ulpCell, { width: '17%' }, getSideStyle(row.sideB)]}>
                                    <Text style={getSideStyle(row.sideB) === styles.redCell ? styles.whiteText : undefined}>
                                        {row.sideB}
                                    </Text>
                                </View>
                                <View style={[styles.ulpCell, { width: '17%' }, getSideStyle(row.sideC)]}>
                                    <Text style={getSideStyle(row.sideC) === styles.redCell ? styles.whiteText : undefined}>
                                        {row.sideC}
                                    </Text>
                                </View>
                                <View style={[styles.ulpCell, { width: '17%' }, getSideStyle(row.sideD)]}>
                                    <Text style={getSideStyle(row.sideD) === styles.redCell ? styles.whiteText : undefined}>
                                        {row.sideD}
                                    </Text>
                                </View>
                            </View>
                        );
                    });
                })}
            </View>
        );
    };

    // Fix renderULPAdditionalStatsTable function to match ship-test-walk.tsx component more closely
    const renderULPAdditionalStatsTable = (sectionData: StatisticsData, title: string) => {
        const additionalStats = sectionData.additionalStats;

        if (!additionalStats || !Array.isArray(additionalStats) || additionalStats.length === 0) {
            return <Text style={styles.noData}>No additional statistics available for {title}</Text>;
        }

        return (
            <View style={[styles.ulpTable, { marginTop: 6 }]}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <View style={[styles.ulpCell, { width: '12%' }]}>
                        <Text>Row Labels</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '10%' }]}>
                        <Text>Data Fill Status</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '12%' }]}>
                        <Text>Overall pass/fail status</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '8%' }]}>
                        <Text>Side A Fail Count</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '8%' }]}>
                        <Text>Side A Pass Count</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '8%' }]}>
                        <Text>Side B Fail Count</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '8%' }]}>
                        <Text>Side B Pass Count</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '8%' }]}>
                        <Text>Side C Fail Count</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '8%' }]}>
                        <Text>Side C Pass Count</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '8%' }]}>
                        <Text>Side D Fail Count</Text>
                    </View>
                    <View style={[styles.ulpCell, { width: '8%' }]}>
                        <Text>Side D Pass Count</Text>
                    </View>
                </View>

                {additionalStats.map((row: any, index: number) => {
                    // Determine overall status style
                    const overallStatusStyle =
                        row.overallStatus === 'Fail' ? styles.redCell :
                            row.overallStatus === 'Pass' ? styles.greenCell : {};

                    return (
                        <View key={`${title}-addstats-${index}`} style={[styles.tableRow, { backgroundColor: '#f9f9f9' }]}>
                            <View style={[styles.ulpCell, { width: '12%' }]}>
                                <Text>{row.rowLabel}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '10%' }]}>
                                <Text>{row.dataFillStatus}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '12%' }, overallStatusStyle]}>
                                <Text style={styles.whiteText}>{row.overallStatus}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '8%' }, parseInt(row.sideAFailCount) > 0 ? styles.redCell : styles.greenCell]}>
                                <Text style={styles.whiteText}>{row.sideAFailCount}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '8%' }, parseInt(row.sideAPassCount) > 0 ? styles.greenCell : styles.redCell]}>
                                <Text style={styles.whiteText}>{row.sideAPassCount}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '8%' }, parseInt(row.sideBFailCount) > 0 ? styles.redCell : styles.greenCell]}>
                                <Text style={styles.whiteText}>{row.sideBFailCount}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '8%' }, parseInt(row.sideBPassCount) > 0 ? styles.greenCell : styles.redCell]}>
                                <Text style={styles.whiteText}>{row.sideBPassCount}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '8%' }, parseInt(row.sideCFailCount) > 0 ? styles.redCell : styles.greenCell]}>
                                <Text style={styles.whiteText}>{row.sideCFailCount}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '8%' }, parseInt(row.sideCPassCount) > 0 ? styles.greenCell : styles.redCell]}>
                                <Text style={styles.whiteText}>{row.sideCPassCount}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '8%' }, parseInt(row.sideDFailCount) > 0 ? styles.redCell : styles.greenCell]}>
                                <Text style={styles.whiteText}>{row.sideDFailCount}</Text>
                            </View>
                            <View style={[styles.ulpCell, { width: '8%' }, parseInt(row.sideDPassCount) > 0 ? styles.greenCell : styles.redCell]}>
                                <Text style={styles.whiteText}>{row.sideDPassCount}</Text>
                            </View>
                        </View>
                    );
                })}
            </View>
        );
    };

    // Fix renderULHStatisticsTable function to match SummaryStackUnitLoadHeight.tsx component more closely
    const renderULHStatisticsTable = (sectionData: StatisticsData, title: string) => {
        const statistics = sectionData.statistics;

        if (!statistics) return null;

        return (
            <View style={styles.ulhTable}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <View style={[styles.ulhCell, { width: '16%' }]}>
                        <Text>Statistic</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '16%' }]}>
                        <Text>Pallet Type</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '17%' }]}>
                        <Text>A-B (in)</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '17%' }]}>
                        <Text>B-C (in)</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '17%' }]}>
                        <Text>C-D (in)</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '17%' }]}>
                        <Text>D-A (in)</Text>
                    </View>
                </View>

                {['max', 'min', 'average', 'stdev', 'count'].map((statType) => {
                    const statData = statistics[statType];
                    if (!statData || !Array.isArray(statData)) return null;

                    return statData.map((row: any, rowIndex: number) => {
                        // Check if post stack or post ship for highlighting values > 1
                        const isPostVariant = title.includes('Stack') || title.includes('Height');
                        const isCountRow = statType === 'count';

                        const getSideStyle = (value: string) => {
                            if (!isPostVariant || isCountRow) return {};
                            return parseFloat(value) > 1 ? styles.redCell : {};
                        };

                        return (
                            <View key={`${title}-${statType}-${rowIndex}`} style={[styles.tableRow, { backgroundColor: '#f9f9f9' }]}>
                                {rowIndex === 0 && (
                                    <View style={[styles.ulhCell, { width: '16%', fontWeight: 'bold' }]}>
                                        <Text>{statType.charAt(0).toUpperCase() + statType.slice(1)}</Text>
                                    </View>
                                )}
                                {rowIndex !== 0 && (
                                    <View style={[styles.ulhCell, { width: '16%' }]}>
                                        <Text></Text>
                                    </View>
                                )}
                                <View style={[styles.ulhCell, { width: '16%' }]}>
                                    <Text>{row.palletType}</Text>
                                </View>
                                <View style={[styles.ulhCell, { width: '17%' }, getSideStyle(row['A-B'])]}>
                                    <Text style={getSideStyle(row['A-B']) === styles.redCell ? styles.whiteText : undefined}>
                                        {row['A-B']}
                                    </Text>
                                </View>
                                <View style={[styles.ulhCell, { width: '17%' }, getSideStyle(row['B-C'])]}>
                                    <Text style={getSideStyle(row['B-C']) === styles.redCell ? styles.whiteText : undefined}>
                                        {row['B-C']}
                                    </Text>
                                </View>
                                <View style={[styles.ulhCell, { width: '17%' }, getSideStyle(row['C-D'])]}>
                                    <Text style={getSideStyle(row['C-D']) === styles.redCell ? styles.whiteText : undefined}>
                                        {row['C-D']}
                                    </Text>
                                </View>
                                <View style={[styles.ulhCell, { width: '17%' }, getSideStyle(row['D-A'])]}>
                                    <Text style={getSideStyle(row['D-A']) === styles.redCell ? styles.whiteText : undefined}>
                                        {row['D-A']}
                                    </Text>
                                </View>
                            </View>
                        );
                    });
                })}
            </View>
        );
    };

    // Fix renderULHAdditionalStatsTable function to match SummaryStackUnitLoadHeight.tsx component more closely
    const renderULHAdditionalStatsTable = (sectionData: StatisticsData, title: string) => {
        const additionalStats = sectionData.additionalStats;

        if (!additionalStats || !Array.isArray(additionalStats) || additionalStats.length === 0) {
            return <Text style={styles.noData}>No additional statistics available for {title}</Text>;
        }

        return (
            <View style={[styles.ulhTable, { marginTop: 6 }]}>
                <View style={[styles.tableRow, styles.tableHeader]}>
                    <View style={[styles.ulhCell, { width: '12%' }]}>
                        <Text>Row Labels</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '10%' }]}>
                        <Text>Data Fill Status</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '12%' }]}>
                        <Text>Overall pass/fail status</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '8%' }]}>
                        <Text>A-B Fail Count</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '8%' }]}>
                        <Text>A-B Pass Count</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '8%' }]}>
                        <Text>B-C Fail Count</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '8%' }]}>
                        <Text>B-C Pass Count</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '8%' }]}>
                        <Text>C-D Fail Count</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '8%' }]}>
                        <Text>C-D Pass Count</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '8%' }]}>
                        <Text>D-A Fail Count</Text>
                    </View>
                    <View style={[styles.ulhCell, { width: '8%' }]}>
                        <Text>D-A Pass Count</Text>
                    </View>
                </View>

                {additionalStats.map((row: any, index: number) => {
                    // Determine overall status style
                    const overallStatusStyle =
                        row.overallStatus === 'Fail' ? styles.redCell :
                            row.overallStatus === 'Pass' ? styles.greenCell : {};

                    return (
                        <View key={`${title}-addstats-${index}`} style={[styles.tableRow, { backgroundColor: '#f9f9f9' }]}>
                            <View style={[styles.ulhCell, { width: '12%' }]}>
                                <Text>{row.rowLabel}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '10%' }]}>
                                <Text>{row.dataFillStatus}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '12%' }, overallStatusStyle]}>
                                <Text style={styles.whiteText}>{row.overallStatus}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '8%' }, parseInt(row['A-BFailCount']) > 0 ? styles.redCell : styles.greenCell]}>
                                <Text style={styles.whiteText}>{row['A-BFailCount']}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '8%' }, parseInt(row['A-BPassCount']) > 0 ? styles.greenCell : styles.redCell]}>
                                <Text style={styles.whiteText}>{row['A-BPassCount']}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '8%' }, parseInt(row['B-CFailCount']) > 0 ? styles.redCell : styles.greenCell]}>
                                <Text style={styles.whiteText}>{row['B-CFailCount']}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '8%' }, parseInt(row['B-CPassCount']) > 0 ? styles.greenCell : styles.redCell]}>
                                <Text style={styles.whiteText}>{row['B-CPassCount']}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '8%' }, parseInt(row['C-DFailCount']) > 0 ? styles.redCell : styles.greenCell]}>
                                <Text style={styles.whiteText}>{row['C-DFailCount']}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '8%' }, parseInt(row['C-DPassCount']) > 0 ? styles.greenCell : styles.redCell]}>
                                <Text style={styles.whiteText}>{row['C-DPassCount']}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '8%' }, parseInt(row['D-AFailCount']) > 0 ? styles.redCell : styles.greenCell]}>
                                <Text style={styles.whiteText}>{row['D-AFailCount']}</Text>
                            </View>
                            <View style={[styles.ulhCell, { width: '8%' }, parseInt(row['D-APassCount']) > 0 ? styles.greenCell : styles.redCell]}>
                                <Text style={styles.whiteText}>{row['D-APassCount']}</Text>
                            </View>
                        </View>
                    );
                })}
            </View>
        );
    };

    return (
        <View style={styles.container}>
            <StandardTitle title="Summary" />

            {sections.map((section, sectionIndex) => {
                const isLeanSection =
                    section.title === 'Pre-Ship Lean' ||
                    section.title === 'Post-Ship Lean' ||
                    section.title.startsWith('Unit Load Stack Lean Weekly');

                const isFTLSection = section.isFTL === true;
                const isULPSection = section.isULP === true;
                const isULHSection = section.isULH === true;

                return (
                    <View key={sectionIndex}>
                        {sectionIndex > 0 && <View style={styles.pageBreak} />}
                        <Text style={styles.subHeading}>{section.title}</Text>

                        {isLeanSection && isNewDataFormat(section.data) ? (
                            // Render the lean section in the new format
                            <View>
                                {renderStatisticsTable(section.data, section.title)}
                                {renderAdditionalStatsTable(section.data, section.title)}
                            </View>
                        ) : isFTLSection && isFTLDataFormat(section.data) ? (
                            // Render the FTL section in the new format
                            <View>
                                {renderFTLPalletDetailsTable(section.data, section.title)}
                                {renderFTLStatisticsTable(section.data, section.title)}
                                {renderFTLAdditionalStatsTable(section.data, section.title)}
                            </View>
                        ) : isULPSection && isULPDataFormat(section.data) ? (
                            // Render the ULP section
                            <View>
                                {renderULPStatisticsTable(section.data, section.title)}
                                {renderULPAdditionalStatsTable(section.data, section.title)}
                            </View>
                        ) : isULHSection && isULPDataFormat(section.data) ? (
                            // Render the ULH section
                            <View>
                                {renderULHStatisticsTable(section.data, section.title)}
                                {renderULHAdditionalStatsTable(section.data, section.title)}
                            </View>
                        ) : (
                            // Render other sections in the original format
                            section.data.additionalStats && section.data.additionalStats.length > 0 ? (
                                <View style={styles.table}>
                                    {/* Table Header */}
                                    <View style={[styles.tableRow, styles.tableHeader]}>
                                        <View style={[styles.tableCell, styles.palletCell]}>
                                            <Text>Pallet Variant</Text>
                                        </View>
                                        <View style={[styles.tableCell, styles.statCell]}>
                                            <Text>Status</Text>
                                        </View>
                                        <View style={[styles.tableCell, styles.statCell]}>
                                            <Text>Max</Text>
                                        </View>
                                        <View style={[styles.tableCell, styles.statCell]}>
                                            <Text>Min</Text>
                                        </View>
                                        <View style={[styles.tableCell, styles.statCell]}>
                                            <Text>Average</Text>
                                        </View>
                                        <View style={[styles.tableCell, styles.statCell]}>
                                            <Text>StDev</Text>
                                        </View>
                                        <View style={[styles.tableCell, styles.statCell]}>
                                            <Text>Pass/Fail</Text>
                                        </View>
                                    </View>

                                    {/* Table Data */}
                                    {section.data.additionalStats.map((item, index) => (
                                        <View key={index} style={styles.tableRow}>
                                            <View style={[styles.tableCell, styles.palletCell]}>
                                                <Text>{item.rowLabel}</Text>
                                            </View>
                                            <View style={[styles.tableCell, styles.statCell]}>
                                                <Text style={getStatusStyle(item.dataFillStatus === 'N/A' || item.dataFillStatus === 'Incomplete'
                                                    ? item.dataFillStatus
                                                    : item.overallStatus)}>
                                                    {item.dataFillStatus === 'N/A' || item.dataFillStatus === 'Incomplete'
                                                        ? item.dataFillStatus
                                                        : item.overallStatus}
                                                </Text>
                                            </View>
                                            <View style={[styles.tableCell, styles.statCell]}>
                                                <Text>{formatValue(item.max)}</Text>
                                            </View>
                                            <View style={[styles.tableCell, styles.statCell]}>
                                                <Text>{formatValue(item.min)}</Text>
                                            </View>
                                            <View style={[styles.tableCell, styles.statCell]}>
                                                <Text>{formatValue(item.average)}</Text>
                                            </View>
                                            <View style={[styles.tableCell, styles.statCell]}>
                                                <Text>{formatValue(item.stdev)}</Text>
                                            </View>
                                            <View style={[styles.tableCell, styles.statCell]}>
                                                <Text>
                                                    {item.count ? `${item.count.pass}/${item.count.fail}/${item.count.total}` : 'N/A'}
                                                </Text>
                                            </View>
                                        </View>
                                    ))}
                                </View>
                            ) : (
                                <Text style={styles.noData}>No data available for {section.title}</Text>
                            )
                        )}
                    </View>
                );
            })}
        </View>
    );
};
